import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import SidebarLink from './SidebarLink';
import SidebarCategory from './SidebarCategory';
import SidebarSection from './SidebarSection';
import t, { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import Config from '../../../config';

const p = partial('SidebarContent');
const s = partial('shared');
const m = partial('matchCenter');

const SidebarContent = ({ me, onClick, collapse, location }) => {
  const config = useConfig();
  const pathname = location?.pathname || '';
  const [active, setActive] = useState('');
  const hideSidebar = (e) => {
    setActive(e);
    onClick();
  };

  const matchSearchQuery = (name) => {
    const { search } = window.location;
    return search?.replace('?', '')?.split('&')?.includes(name);
  };

  const matchPathname = (name) => {
    const { pathname } = window.location;
    return pathname?.split('/')?.includes(name);
  };

  const renderPermissions = () => {
    const { permissions } = me ?? [];
    if (permissions) {
      const features = config?.featureFlags;
      let matches;
      let users;
      let packages;
      let sales;
      let reports;
      let offers;
      let news;
      let dashboard;
      let companies;
      let data;
      const subMenuStyles = { background: '#F5FAFF' };

      const contentAccess =
        permissions.includes('access-matches') ||
        permissions.includes('access-news');

      const ticketsAccess =
        permissions.includes('access-tickets') ||
        permissions.includes('access-season-tickets');
      const matchAndSeasonAccess = permissions.includes('access-matches');
      const newsAccess = permissions.includes('access-news');
      const messagesAccess = permissions.includes('access-messages');
      const chatsAccess =
        messagesAccess ||
        permissions.includes('access-chat') ||
        permissions.includes('access-chats');

      const boxOfficeAccess = permissions.includes('access-sales');
      const packagesAccess = permissions.includes('access-settings');
      const importAccess = permissions.includes('access-tickets');
      const stadiumAccess =
        permissions.includes('access-stadium') ||
        permissions.includes('access-settings');
      const settingsAccess = permissions.includes('access-settings');

      const userAccess = permissions.includes('access-users');
      const companyAccess = permissions.includes('access-companies');

      const kioskAccess = permissions.includes('access-kiosks');
      const kioskSaleAccess = permissions.includes('access-kiosk-sales');

      const invoiceAccess =
        permissions.includes('access-payments') &&
        permissions.includes('access-teams') &&
        permissions.includes('access-matches');
      const reportsAccess =
        permissions.includes('access-reports') &&
        permissions.includes('access-teams') &&
        permissions.includes('access-matches');

      if (contentAccess) {
        matches = (
          <SidebarSection
            key="matches-sidebar"
            id="content"
            title={s('content')}
            routes={[
              '/matches',
              '/events',
              '/offers',
              '/questionnaires',
              '/chats',
              '/news',
              '/players',
            ]}
            pathname={pathname}
            collapse={collapse}
          >
            {matchAndSeasonAccess && Config.monolithEnabled && (
              <SidebarLink
                title={p('matches')}
                active={active === 'matches'}
                newIcon="matches"
                route="/matches"
                onClick={() => {
                  hideSidebar('matches');
                }}
              />
            )}
            {matchAndSeasonAccess && !Config.monolithEnabled && (
              <SidebarLink
                title={m('matchCenter')}
                active={active === 'matches'}
                newIcon="match"
                route="/matches/upcoming"
                onClick={() => {
                  hideSidebar('matches');
                }}
              />
            )}
            <SidebarLink
              title={p('events')}
              active={active === 'events' || matchPathname('events')}
              open={active.split('_')[0] === 'events'}
              newIcon="event"
              route="/events"
              onClick={() => {
                hideSidebar('events');
              }}
            />
            {active.split('_')[0] === '1events' && matchPathname('events') && (
              <>
                <SidebarLink
                  title={t('EventsPage.eventCategories')}
                  active={matchSearchQuery('tab=2')}
                  icon="dot"
                  style={subMenuStyles}
                  route="/events?tab=2"
                  onClick={() => {
                    hideSidebar('events_2');
                  }}
                />
                <SidebarLink
                  title={s('communication')}
                  icon="dot"
                  active={matchSearchQuery('tab=3')}
                  style={subMenuStyles}
                  route="/events?tab=3"
                  onClick={() => {
                    hideSidebar('events_3');
                  }}
                />
              </>
            )}
            <SidebarLink
              title={p('offers')}
              newIcon="offers"
              open={active.split('_')[0] === 'offers'}
              active={active === 'offers'}
              route="/offers"
              onClick={() => {
                hideSidebar('offers');
              }}
            />
            <SidebarLink
              title={p('questionaire')}
              newIcon="quiz"
              open={active.split('_')[0] === 'questionnaires'}
              active={active === 'questionaires'}
              route="/questionnaires"
              onClick={() => {
                hideSidebar('questionaires');
              }}
            />
            {chatsAccess && (
              <SidebarLink
                title={t('chat.chats')}
                active={active === 'chats' || matchPathname('chats')}
                newIcon="chats"
                route="/chats"
                onClick={() => {
                  hideSidebar('chats');
                }}
              />
            )}
            {active.split('_')[0] === '1offers' && (
              <>
                <SidebarLink
                  title={s('communication')}
                  icon="dot"
                  active={active === 'offers_2'}
                  style={subMenuStyles}
                  route="/offers?tab=2"
                  onClick={() => {
                    hideSidebar('offers_2');
                  }}
                />
              </>
            )}
            {newsAccess && (
              <>
                <SidebarLink
                  title={s('news')}
                  open={active.split('_')[0] === 'news'}
                  active={active === 'news'}
                  newIcon="news"
                  route="/news?tab=1"
                  onClick={() => {
                    hideSidebar('news');
                  }}
                />
                {active.split('_')[0] === '1news' && (
                  <>
                    <SidebarLink
                      title={s('communication')}
                      icon="dot"
                      active={active === 'news_2'}
                      style={subMenuStyles}
                      route="/news?tab=2"
                      onClick={() => {
                        hideSidebar('news_2');
                      }}
                    />
                    <SidebarLink
                      title={s('settings')}
                      icon="dot"
                      active={active === 'news_3'}
                      style={subMenuStyles}
                      route="/news?tab=3"
                      onClick={() => {
                        hideSidebar('news_3');
                      }}
                    />
                  </>
                )}
              </>
            )}
            <SidebarLink
              title={s('players')}
              open={active.split('_')[0] === 'players'}
              active={active === 'players'}
              newIcon="players"
              route="/players?tab=1"
              onClick={() => {
                hideSidebar('players');
              }}
            />
            {active.split('_')[0] === '1players' && (
              <>
                <SidebarLink
                  title={s('settings')}
                  icon="dot"
                  active={active === 'players_2'}
                  style={subMenuStyles}
                  route="/players?tab=2"
                  onClick={() => {
                    hideSidebar('players_2');
                  }}
                />
                <SidebarLink
                  title={t('PlayerListPage.transferPlayers')}
                  icon="dot"
                  active={active === 'players_3'}
                  style={subMenuStyles}
                  route="/players?tab=3"
                  onClick={() => {
                    hideSidebar('players_3');
                  }}
                />
              </>
            )}
          </SidebarSection>
        );
      }

      if (
        (ticketsAccess || boxOfficeAccess || packagesAccess) &&
        features?.includes('tickets') &&
        Config.monolithEnabled
      ) {
        packages = (
          <SidebarSection
            key="tickets-sidebar"
            id="tickets"
            title={s('tickets')}
            routes={[
              '/tickets',
              '/box',
              '/packages',
              '/discounts',
              '/stadiums',
            ]}
            pathname={pathname}
            collapse={collapse}
          >
            {ticketsAccess && (
              <>
                <SidebarLink
                  title={s('tickets')}
                  open={active.split('_')[0] === 'tickets'}
                  active={active === 'tickets'}
                  newIcon="ticket"
                  route="/tickets?tab=1"
                  onClick={() => {
                    hideSidebar('tickets');
                  }}
                />
                {active.split('_')[0] === '1tickets' && (
                  <>
                    <SidebarLink
                      title={t('TicketsPage.matchTickets')}
                      icon="dot"
                      active={active === 'tickets_2'}
                      style={subMenuStyles}
                      route="/tickets?tab=2"
                      onClick={() => {
                        hideSidebar('tickets_2');
                      }}
                    />
                  </>
                )}
                {boxOfficeAccess && (
                  <SidebarLink
                    title={p('boxOffice')}
                    active={active === 'boxOffice'}
                    newIcon="cashier"
                    route="/box"
                    onClick={() => {
                      hideSidebar('boxOffice');
                    }}
                  />
                )}
                <SidebarLink
                  title={p('packages')}
                  active={active === 'package'}
                  newIcon="packages"
                  route="/packages"
                  onClick={() => {
                    hideSidebar('package');
                  }}
                />
                <SidebarLink
                  title={s('discounts')}
                  open={active.split('_')[0] === 'discounts'}
                  active={active === 'discounts'}
                  newIcon="discounts"
                  route="/discounts"
                  onClick={() => {
                    hideSidebar('discounts');
                  }}
                />
                {active.split('_')[0] === '1discounts' && (
                  <>
                    <SidebarLink
                      title={t('DiscountsPage.discountCodes')}
                      icon="dot"
                      active={active === 'discounts_2'}
                      style={subMenuStyles}
                      route="/discounts?tab=2"
                      onClick={() => {
                        hideSidebar('discounts_2');
                      }}
                    />
                  </>
                )}
                {features?.includes('tickets') && (
                  <>
                    <SidebarCategory title={p('settings')} icon="cog">
                      <SidebarLink
                        title={s('priceGroups')}
                        newIcon="priceGroups"
                        route="/settings?tab=2"
                        onClick={() => {
                          hideSidebar('');
                        }}
                      />
                      <SidebarLink
                        title={p('stadiums')}
                        newIcon="stadiums"
                        route="/stadiums?tab=1"
                        onClick={() => {
                          hideSidebar('');
                        }}
                      />
                      <SidebarLink
                        title={p('controlImages')}
                        newIcon="controlImages"
                        route="/settings?tab=4"
                        onClick={() => {
                          hideSidebar('');
                        }}
                      />
                      {importAccess ? (
                        <SidebarLink
                          title={s('import')}
                          newIcon="import"
                          route={`/settings?tab=${settingsAccess ? 8 : 1}`}
                          onClick={() => {
                            hideSidebar('');
                          }}
                        />
                      ) : undefined}
                    </SidebarCategory>
                  </>
                )}
              </>
            )}
          </SidebarSection>
        );
      }

      if (permissions.includes('access-news') || chatsAccess) {
        news = (
          <SidebarSection
            key="news-sidebar"
            id="app"
            title={p('app')}
            routes={['/app_layout', '/web_layout', '/spotlight', '/messages']}
            pathname={pathname}
            collapse={collapse}
          >
            {permissions.includes('access-news') && (
              <>
                <SidebarLink
                  title={p('appLayout')}
                  active={active === 'applayout'}
                  newIcon="layout"
                  route="/app_layout"
                  onClick={() => {
                    hideSidebar('applayout');
                  }}
                />
                {(Config.clubWebsiteEnabled ||
                  features?.includes('website')) && (
                  <SidebarLink
                    title={p('webLayout')}
                    active={active === 'weblayout'}
                    newIcon="weblayout"
                    route="/web_layout"
                    onClick={() => {
                      hideSidebar('weblayout');
                    }}
                  />
                )}
                <SidebarLink
                  title={s('spotlight')}
                  active={active === 'spotlight'}
                  collapse={collapse}
                  newIcon="spotlight"
                  route="/spotlight"
                  onClick={() => {
                    hideSidebar('spotlight');
                  }}
                />
              </>
            )}
            {messagesAccess && (
              <>
                <SidebarLink
                  title={p('sendMessages')}
                  open={active.split('_')[0] === 'messages'}
                  active={active === 'messages'}
                  newIcon="communication"
                  route="/messages"
                  onClick={() => {
                    hideSidebar('messages');
                  }}
                />
                {active.split('_')[0] === '1messages' && (
                  <>
                    <SidebarLink
                      title={t('MessagesStatsPage.pushStats')}
                      icon="dot"
                      active={active === 'messages_2'}
                      style={subMenuStyles}
                      route="/messages?tab=2"
                      onClick={() => {
                        hideSidebar('messages_2');
                      }}
                    />
                    <SidebarLink
                      title={t('MessagesPage.appMessage')}
                      icon="dot"
                      active={active === 'messages_3'}
                      style={subMenuStyles}
                      route="/messages?tab=3"
                      onClick={() => {
                        hideSidebar('messages_3');
                      }}
                    />
                    <SidebarLink
                      title={s('communication')}
                      icon="dot"
                      active={active === 'messages_4'}
                      style={subMenuStyles}
                      route="/messages?tab=4"
                      onClick={() => {
                        hideSidebar('messages_4');
                      }}
                    />
                  </>
                )}
              </>
            )}
          </SidebarSection>
        );
      }

      if (userAccess) {
        users = (
          <SidebarSection
            key="users-sidebar"
            id="users"
            title={s('users')}
            routes={['/users', '/user_tiers']}
            pathname={pathname}
            collapse={collapse}
          >
            {userAccess && (
              <>
                <SidebarLink
                  title={s('users')}
                  open={active.split('_')[0] === 'users'}
                  active={active === 'users'}
                  newIcon="users"
                  route="/users?tab=1"
                  onClick={() => {
                    hideSidebar('users');
                  }}
                />
                {active.split('_')[0] === '1users' && (
                  <>
                    <SidebarLink
                      title={s('userGroups')}
                      icon="dot"
                      active={active === 'users_2'}
                      style={subMenuStyles}
                      route="/users?tab=2"
                      onClick={() => {
                        hideSidebar('users_2');
                      }}
                    />
                    <SidebarLink
                      title={s('roles')}
                      icon="dot"
                      active={active === 'users_3'}
                      style={subMenuStyles}
                      route="/users?tab=3"
                      onClick={() => {
                        hideSidebar('users_3');
                      }}
                    />
                  </>
                )}
                <SidebarLink
                  title={s('userTiers')}
                  active={active === 'userTiers'}
                  newIcon="tiers"
                  route="/user_tiers?tab=1"
                  onClick={() => {
                    hideSidebar('userTiers');
                  }}
                />
              </>
            )}
          </SidebarSection>
        );
      }

      if (companyAccess) {
        companies = (
          <SidebarSection
            key="companies-sidebar"
            id="companies"
            title={s('companies')}
            routes={['/companies', '/deals', '/sponsor']}
            pathname={pathname}
            collapse={collapse}
          >
            {companyAccess && (
              <>
                <SidebarLink
                  title={s('companies')}
                  open={active.split('_')[0] === 'companies'}
                  active={active === 'companies'}
                  newIcon="companies"
                  route="/companies?tab=1"
                  onClick={() => {
                    hideSidebar('companies');
                  }}
                />
                {active.split('_')[0] === '1companies' && (
                  <>
                    <SidebarLink
                      title={s('settings')}
                      icon="dot"
                      active={active === 'companies_2'}
                      style={subMenuStyles}
                      route="/companies?tab=2"
                      onClick={() => {
                        hideSidebar('companies_2');
                      }}
                    />
                    <SidebarLink
                      title="App sponsor"
                      icon="dot"
                      active={active === 'companies_3'}
                      style={subMenuStyles}
                      route="/companies?tab=3"
                      onClick={() => {
                        hideSidebar('companies_3');
                      }}
                    />
                    <SidebarLink
                      title={t('Category.goalService')}
                      icon="dot"
                      active={active === 'companies_4'}
                      style={subMenuStyles}
                      route="/companies?tab=4"
                      onClick={() => {
                        hideSidebar('companies_4');
                      }}
                    />
                  </>
                )}
                <SidebarLink
                  title={s('deals')}
                  active={active === 'deals'}
                  disabled={false} /*!features?.includes('deals')*/
                  collapse={collapse}
                  newIcon="partner_activities"
                  route="/deals"
                  onClick={() => {
                    hideSidebar('deals');
                  }}
                />
                <SidebarLink
                  title={s('sponsor')}
                  active={active === 'sponsor'}
                  disabled={false} /*!features?.includes('deals')*/
                  collapse={collapse}
                  newIcon="sponsor"
                  route="/sponsor"
                  onClick={() => {
                    hideSidebar('sponsor');
                  }}
                />
              </>
            )}
          </SidebarSection>
        );
      }

      if (userAccess || settingsAccess) {
        data = (
          <SidebarSection
            key="data-sidebar"
            id="data"
            title={p('data')}
            routes={['/user_analytics', '/transactions', '/quarterly-review']}
            pathname={pathname}
            collapse={collapse}
          >
            {userAccess && (
              <SidebarLink
                title={t('Analytics.analytics')}
                active={active === 'analytics'}
                newIcon="user_analytics"
                route="/user_analytics"
                onClick={() => {
                  hideSidebar('analytics');
                }}
              />
            )}
            {userAccess && (
              <SidebarLink
                title={s('transactions')}
                active={active === 'transactions'}
                newIcon="transactions"
                route="/transactions"
                onClick={() => {
                  hideSidebar('transactions');
                }}
              />
            )}
            {settingsAccess && (
              <SidebarLink
                title={p('quarterlyReview')}
                newIcon="quarterlyReview"
                active={active === 'quarterlyReview'}
                route="/quarterly-review"
                onClick={() => {
                  hideSidebar('quarterlyReview');
                }}
              />
            )}
          </SidebarSection>
        );
      }

      if (kioskAccess || kioskSaleAccess || boxOfficeAccess) {
        sales = (
          <SidebarSection
            key="sales-sidebar"
            id="sales"
            title={p('salesAndFundraising')}
            routes={[
              '/cnc',
              '/kiosk',
              '/auctions',
              '/external_stores',
              '/lottery',
              '/tickets/external',
              '/goal-sponsor',
            ]}
            pathname={pathname}
            collapse={collapse}
          >
            {kioskAccess && kioskSaleAccess && Config.monolithEnabled && (
              <SidebarLink
                title={`${p('c&c')} - ${s('kiosk')}`}
                active={active === 'cnc'}
                premium
                collapse={collapse}
                newIcon="cart"
                route="/cnc?tab=1"
                onClick={() => {
                  hideSidebar('cnc');
                }}
              />
            )}
            {kioskSaleAccess && kioskAccess && Config.monolithEnabled && (
              <SidebarLink
                title={`${p('c&c')} - ${p('salesPanel')}`}
                active={active === 'cnc_2'}
                premium
                collapse={collapse}
                newIcon="cart"
                route="/kiosk"
                onClick={() => {
                  hideSidebar('cnc_2');
                }}
              />
            )}
            {kioskAccess && (
              <SidebarLink
                title={p('auctions')}
                active={active === 'auctions'}
                premium
                collapse={collapse}
                newIcon="auctions"
                route="/auctions"
                onClick={() => {
                  hideSidebar('auctions');
                }}
              />
            )}

            {kioskSaleAccess && Config.monolithEnabled && (
              <SidebarLink
                title={p('externalStores')}
                active={active === 'externalStores'}
                premium
                collapse={collapse}
                newIcon="store"
                route="/external_stores"
                onClick={() => {
                  hideSidebar('externalStores');
                }}
              />
            )}
            <SidebarLink
              title={s('lottery')}
              active={active === 'lottery'}
              premium
              collapse={collapse}
              newIcon="lottery"
              route="/lottery"
              onClick={() => {
                hideSidebar('lottery');
              }}
            />
            {ticketsAccess && (
              <SidebarLink
                title={p('externalTickets')}
                active={active === 'externalTickets'}
                newIcon="externalTickets"
                premium
                collapse={collapse}
                route="/tickets/external"
                onClick={() => {
                  hideSidebar('externalTickets');
                }}
              />
            )}
            <SidebarLink
              title={t('goalSponsor.goalSponsor')}
              active={active === 'goal-sponsor'}
              newIcon="goalSponsor"
              premium
              collapse={collapse}
              route="/goal-sponsor"
              onClick={() => {
                hideSidebar('goal-sponsor');
              }}
            />
          </SidebarSection>
        );
      }

      const settings = (
        <SidebarSection
          key="settings"
          id="settings"
          title={s('settings')}
          routes={['/profile', '/applinks', '/seasons', '/settings']}
          pathname={pathname}
          collapse={collapse}
        >
          <>
            {/*settingsAccess && (
                <SidebarLink
                  title={t('shared.vendors')}
                  route="/vendors"
                  onClick={() => {
                    hideSidebar('');
                  }}
                />
              )*/}
            {settingsAccess && (
              <SidebarLink
                title={t('ClubProfilePage.clubProfile')}
                newIcon={'clubprofile'}
                route="/profile"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
            {settingsAccess && (
              <SidebarLink
                title={t('ClubProfilePage.appLinks')}
                newIcon={'qrcode'}
                route="/applinks"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
            {settingsAccess && Config.monolithEnabled && (
              <SidebarLink
                title={s('seasons')}
                newIcon="seasons"
                route="/seasons"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
            {settingsAccess && Config.monolithEnabled && (
              <SidebarLink
                title={s('team')}
                newIcon="team"
                route="/settings?tab=1"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
            {settingsAccess && Config.monolithEnabled && (
              <SidebarLink
                title={s('priceGroups')}
                newIcon="priceGroups"
                route="/settings?tab=2"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
            {settingsAccess && Config.monolithEnabled && (
              <SidebarLink
                title={p('matchTypes')}
                newIcon="matchTypes"
                route="/settings?tab=3"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
            {settingsAccess && Config.monolithEnabled && (
              <SidebarLink
                title={t('settingsObjectsShared.controllImages')}
                newIcon="controlImages"
                route="/settings?tab=4"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
            {settingsAccess && (
              <SidebarLink
                title={s('userterms')}
                newIcon={'userterms'}
                route={`/settings?tab=${Config.monolithEnabled ? '5' : '1'}`}
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
            {ticketsAccess && Config.monolithEnabled && (
              <SidebarLink
                title={s('import')}
                newIcon="import"
                route="/settings?tab=6"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
          </>
        </SidebarSection>
      );

      if (
        (invoiceAccess ||
          reportsAccess ||
          stadiumAccess ||
          settingsAccess ||
          permissions.includes('access-settings')) &&
        Config.monolithEnabled
      ) {
        reports = (
          <ul key="settings-sidebar" className="sidebar__block">
            {reportsAccess && (
              <SidebarLink
                title={p('reports')}
                newIcon="reports"
                route="/reports?tab=1"
                onClick={() => {
                  hideSidebar('');
                }}
              />
            )}
          </ul>
        );
      }

      if (permissions.includes('access-settings')) {
        dashboard = (
          <ul key="dashboard-sidebar" className="sidebar__block border-none">
            {permissions.includes('super-admin') && (
              <SidebarLink
                title={p('clubAdministration')}
                newIcon="clubAdmin"
                onClick={() => {
                  hideSidebar('');
                }}
                route="/clubadmin"
              />
            )}
            <SidebarLink
              title={s('dashboard')}
              active={active === 'dashboard'}
              newIcon="dashboard"
              route="/"
              onClick={() => {
                hideSidebar('dashboard');
              }}
            />
          </ul>
        );
      }

      return [
        dashboard,
        matches,
        packages,
        news,
        sales,
        users,
        companies,
        data,
        reports,
        settings,
        offers,
      ];
    }

    return '';
  };

  return <div className="sidebar__content">{renderPermissions()}</div>;
};

SidebarContent.defaultProps = {
  me: null,
};

SidebarContent.propTypes = {
  onClick: PropTypes.func.isRequired,
  me: PropTypes.shape({
    permissions: PropTypes.arrayOf(PropTypes.string),
  }),
  balance: PropTypes.shape({
    name: PropTypes.string.isRequired,
    hasFetched: PropTypes.bool.isRequired,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  enabledFeatures: PropTypes.shape({
    fansaldo: PropTypes.bool,
    packages: PropTypes.bool,
    hasFetched: PropTypes.bool.isRequired,
  }).isRequired,
};

export default withRouter(
  connect((state) => ({
    me: state.admin.me,
    balance: state.balance,
    enabledFeatures: state.enabledFeatures,
  }))(SidebarContent)
);
